/**
 * ConsolePanel — Shared console toolbar + output area for System Response modals.
 *
 * Renders the toolbar (line count, metadata, wrap/copy/scroll buttons) and the
 * scrollable console output with standardized line prefix coloring.
 */
import type { ReactNode, RefObject } from "react";

import { Button } from "../ui/Button";
import { Spinner } from "../ui/Spinner";
import { cn } from "../../utils/cn";

const getLineClassName = (line: string): string | undefined => {
  const lower = line.toLowerCase();
  if (lower.startsWith("[error]")) return "text-red-600 dark:text-red-400";
  if (lower.startsWith("[warn]")) return "text-yellow-600 dark:text-yellow-400/80";
  if (lower.startsWith("[info]")) return "text-blue-600 dark:text-blue-400/70";
  return undefined;
};

type ConsolePanelProps = {
  lines: string[];
  consoleRef: RefObject<HTMLDivElement>;
  onScroll: () => void;
  wrapLines: boolean;
  onToggleWrap: () => void;
  autoScroll: boolean;
  onScrollToBottom: () => void;
  onCopy: () => void;
  // Toolbar metadata (all optional)
  durationMs?: number | null;
  exitCode?: number | null;
  progress?: number;
  stepLabel?: string | null;
  jobIdShort?: string;
  // State
  isLoading?: boolean;
  // Slots
  afterLines?: ReactNode;
  error?: string | null;
};

export const ConsolePanel = ({
  lines,
  consoleRef,
  onScroll,
  wrapLines,
  onToggleWrap,
  autoScroll,
  onScrollToBottom,
  onCopy,
  durationMs,
  exitCode,
  progress,
  stepLabel,
  jobIdShort,
  isLoading,
  afterLines,
  error,
}: ConsolePanelProps) => (
  <>
    {/* Console toolbar */}
    <div className="flex items-center justify-between gap-2 px-4 py-2 border-b border-white/[0.04] bg-white/[0.01]">
      <div className="flex items-center gap-2 text-[10px] text-muted-foreground/70">
        <span>{lines.length} lines</span>
        {durationMs != null && <span>· {(durationMs / 1000).toFixed(1)}s</span>}
        {exitCode != null && <span>· exit {exitCode}</span>}
        {progress != null && progress > 0 && progress < 100 && <span>· {progress}%</span>}
        {stepLabel && <span>· {stepLabel}</span>}
        {jobIdShort && (
          <span>· Job <span className="font-mono">{jobIdShort}</span></span>
        )}
      </div>
      <div className="flex items-center gap-1">
        <Button
          variant="ghost"
          size="sm"
          className="h-6 px-2 text-[10px]"
          onClick={onToggleWrap}
        >
          {wrapLines ? "No Wrap" : "Wrap"}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="h-6 px-2 text-[10px]"
          onClick={onCopy}
          disabled={lines.length === 0}
        >
          Copy
        </Button>
        {!autoScroll && (
          <Button
            variant="ghost"
            size="sm"
            className="h-6 px-2 text-[10px]"
            onClick={onScrollToBottom}
          >
            Scroll to bottom
          </Button>
        )}
      </div>
    </div>

    {/* Console output */}
    <div
      ref={consoleRef}
      onScroll={onScroll}
      className={cn(
        "h-[400px] overflow-y-auto overflow-x-auto qb-scroll p-4",
        "bg-card/95 font-mono text-[11px] leading-[1.6] text-foreground",
        wrapLines ? "whitespace-pre-wrap break-all" : "whitespace-pre",
      )}
    >
      {lines.length === 0 && isLoading && (
        <div className="flex items-center gap-2 text-muted-foreground/50">
          <Spinner size="3.5" />
          <span>Waiting for output...</span>
        </div>
      )}
      {lines.map((line, i) => (
        <div key={i} className={getLineClassName(line)}>
          {line}
        </div>
      ))}
      {error && (
        <div className="mt-2 text-yellow-400/80">[info] {error}</div>
      )}
      {afterLines}
    </div>
  </>
);
